import { API_ENDPOINTS, ERROR_MESSAGES } from '../constants';

/**
 * Weather Service
 * Handles all weather API calls
 */

/**
 * Fetch weather data for a location
 * @param {string} location - The location to fetch weather for
 * @returns {Promise<Object>} - Weather data object
 */
export const fetchWeatherData = async (location) => {
  if (!location) {
    throw new Error('Location is required');
  }

  try {
    const response = await fetch(
      `${API_ENDPOINTS.WEATHER}/${encodeURIComponent(location)}?format=j1`
    );

    if (!response.ok) {
      throw new Error('Weather data not available');
    }

    const data = await response.json();

    // Transform current weather
    const current = data.current_condition[0];
    const currentWeather = {
      location: location,
      temperature: parseInt(current.temp_C),
      feelsLike: parseInt(current.FeelsLikeC),
      condition: current.weatherDesc[0].value,
      humidity: current.humidity,
      windSpeed: current.windspeedKmph,
      windDirection: current.winddir16Point,
      visibility: current.visibility,
      pressure: current.pressure,
      uvIndex: current.uvIndex,
    };

    // Transform forecast data
    const forecastData = data.weather.map(day => ({
      date: day.date,
      maxTemp: parseInt(day.maxtempC),
      minTemp: parseInt(day.mintempC),
      condition: day.hourly[4].weatherDesc[0].value,
      humidity: day.hourly[4].humidity,
      windSpeed: day.hourly[4].windspeedKmph,
      windDirection: day.hourly[4].winddir16Point,
      chanceOfRain: day.hourly[4].chanceofrain,
    }));

    return {
      current: currentWeather,
      forecast: forecastData,
    };
  } catch (error) {
    console.error('Weather fetch error:', error);
    throw new Error(ERROR_MESSAGES.WEATHER_FETCH_FAILED);
  }
};

/**
 * Fetch location suggestions
 * @param {string} query - The search query
 * @returns {Promise<Array>} - Array of location suggestions
 */
export const fetchLocationSuggestions = async (query) => {
  if (!query || query.length < 2) {
    return [];
  }

  const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
  
  if (!apiKey) {
    console.error('Geoapify API key is missing');
    return [];
  }

  try {
    const response = await fetch(
      `${API_ENDPOINTS.GEOCODE}?text=${encodeURIComponent(query)}&apiKey=${apiKey}`
    );

    if (!response.ok) {
      throw new Error('Unable to fetch location suggestions');
    }

    const data = await response.json();
    return data.features.map(feature => feature.properties.formatted);
  } catch (error) {
    console.error('Location suggestions error:', error);
    throw new Error(ERROR_MESSAGES.LOCATION_FETCH_FAILED);
  }
};

/**
 * Get location from coordinates
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @returns {Promise<string>} - Location name
 */
export const getLocationFromCoordinates = async (latitude, longitude) => {
  const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
  
  if (!apiKey) {
    throw new Error('Geoapify API key is missing');
  }

  try {
    const response = await fetch(
      `https://api.geoapify.com/v1/geocode/reverse?lat=${latitude}&lon=${longitude}&apiKey=${apiKey}`
    );

    if (!response.ok) {
      throw new Error('Unable to fetch location');
    }

    const data = await response.json();
    if (data.features && data.features.length > 0) {
      return data.features[0].properties.city || 
             data.features[0].properties.state || 
             data.features[0].properties.country;
    }
    
    throw new Error('Location not found');
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    throw new Error(ERROR_MESSAGES.LOCATION_FETCH_FAILED);
  }
};
